/**
 * /learn/compound — Interactive compound interest lesson.
 *
 * Teaches the "aha moment" that saving $5/day at age 15 becomes more than
 * saving $50/day starting at 35. Uses pure JS math (no backend hit — instant).
 */
import React, { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, Legend } from "recharts";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import BookStrip from "@/components/BookStrip";
import useSEO from "@/lib/useSEO";
import QuickAnswer from "@/components/QuickAnswer";

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtShort = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}k`
  : `$${Math.round(n)}`;

const compound = ({ startAge, monthly, rate, endAge = 65 }) => {
  const years = Math.max(0, endAge - startAge);
  const monthlyRate = rate / 12;
  let balance = 0;
  const path = [{ age: startAge, balance: 0 }];
  for (let y = 1; y <= years; y++) {
    for (let m = 0; m < 12; m++) {
      balance = balance * (1 + monthlyRate) + monthly;
    }
    path.push({ age: startAge + y, balance });
  }
  return { final: balance, path };
};

export default function LearnCompound() {
  useSEO({
    title: "Compound Interest — Learn · Centennial",
    description: "Interactive lesson on how starting to save early beats saving more later. See the compound interest 'aha moment' with your own numbers.",
    canonicalPath: "/learn/compound",
  });

  const [monthly, setMonthly] = useState(150);
  const [startAge, setStartAge] = useState(15);
  const rate = 0.07; // Real market average

  // Comparison paths
  const early = useMemo(() => compound({ startAge, monthly, rate }), [startAge, monthly]);
  const late = useMemo(() => compound({ startAge: 35, monthly: monthly * 3, rate }), [monthly]);

  // Align on same X-axis (age 15 → 65)
  const merged = [];
  const earlyMap = new Map(early.path.map((p) => [p.age, p.balance]));
  const lateMap = new Map(late.path.map((p) => [p.age, p.balance]));
  for (let age = 15; age <= 65; age++) {
    merged.push({
      age,
      early: earlyMap.get(age) ?? null,
      late: lateMap.get(age) ?? null,
    });
  }

  const diff = early.final - late.final;
  const earlyContrib = monthly * 12 * (65 - startAge);
  const lateContrib = monthly * 3 * 12 * (65 - 35);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link
          to="/learn"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> Back to lessons
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
          Lesson 1 · 5 min read
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] mb-4">
          The trick that rich people know<br />and no one taught you in school.
        </h1>
        <p className="text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          Save <span className="text-[#2B5339] font-semibold">$5 a day starting at 15</span>. Or save <span className="text-[#2B5339] font-semibold">$15 a day starting at 35</span>. You end up with more money doing the first. That&apos;s the trick. Here&apos;s why.
        </p>

        <QuickAnswer>
          Compound interest means your returns earn their own returns. Because growth multiplies
          over time, money invested early beats larger amounts invested later — $150/month from
          age 15 typically ends up ahead of $450/month from age 35. Time in the market matters
          more than the amount or the timing of any single contribution.
        </QuickAnswer>

        {/* Interactive chart */}
        <div className="mt-10 bg-white border border-[#E5E2DC] rounded-2xl p-6">
          <div className="flex flex-wrap items-end gap-5 mb-6">
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                What you can save per month
              </Label>
              <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg">
                <span className="pl-3 text-[#5C6B62] text-sm">$</span>
                <Input
                  data-testid="learn-monthly"
                  type="number"
                  value={monthly}
                  onChange={(e) => setMonthly(Number(e.target.value) || 0)}
                  className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-24"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                Age you start saving
              </Label>
              <div className="mt-1.5 flex items-center bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg">
                <Input
                  data-testid="learn-startage"
                  type="number"
                  value={startAge}
                  onChange={(e) => setStartAge(Number(e.target.value) || 15)}
                  className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono-num text-lg py-2 w-20"
                />
                <span className="pr-3 text-[#5C6B62] text-sm">yrs</span>
              </div>
            </div>
            <div className="text-xs text-[#5C6B62] pb-2">
              Assumes 7%/yr real returns (roughly the long-run average of the S&P 500 after inflation)
            </div>
          </div>

          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={merged} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
              <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} />
              <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#5C6B62" }} axisLine={{ stroke: "#E5E2DC" }} />
              <Tooltip
                formatter={(v) => (typeof v === "number" ? fmt(v) : v)}
                labelFormatter={(age) => `Age ${age}`}
                contentStyle={{ backgroundColor: "#fff", border: "1px solid #E5E2DC", borderRadius: 8 }}
              />
              <Legend />
              <ReferenceLine x={35} stroke="#5C6B62" strokeDasharray="3 3" label={{ value: "Late saver starts", fontSize: 10, fill: "#5C6B62" }} />
              <Line
                type="monotone"
                dataKey="early"
                stroke="#2B5339"
                strokeWidth={3}
                dot={false}
                name={`Start at ${startAge}, ${fmt(monthly)}/mo`}
              />
              <Line
                type="monotone"
                dataKey="late"
                stroke="#C26747"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                name={`Start at 35, ${fmt(monthly * 3)}/mo (3× as much!)`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Numbers */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E2DC] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-2">
              Early saver at 65
            </div>
            <div className="font-mono-num text-2xl text-[#2B5339]" data-testid="learn-early-final">
              {fmt(early.final)}
            </div>
            <div className="text-xs text-[#5C6B62] mt-1">
              You put in {fmt(earlyContrib)}
            </div>
          </div>
          <div className="bg-white border border-[#E5E2DC] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-2">
              Late saver at 65
            </div>
            <div className="font-mono-num text-2xl text-[#C26747]" data-testid="learn-late-final">
              {fmt(late.final)}
            </div>
            <div className="text-xs text-[#5C6B62] mt-1">
              You put in {fmt(lateContrib)}
            </div>
          </div>
          <div className="bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#2B5339] font-semibold mb-2 inline-flex items-center gap-1">
              <Sparkles size={11} /> The aha
            </div>
            <div className="font-mono-num text-2xl text-[#1C2B23]" data-testid="learn-diff">
              {diff >= 0 ? "+" : "-"}{fmt(Math.abs(diff))}
            </div>
            <div className="text-xs text-[#1C2B23] mt-1 leading-relaxed">
              {diff >= 0
                ? "The early saver wins — by putting in LESS money over their life."
                : "Late saver catches up because they contribute so much more. But they gave up 20 years of freedom to do it."}
            </div>
          </div>
        </div>

        {/* Prose */}
        <div className="mt-10 prose prose-sm max-w-2xl text-[#1C2B23] space-y-4 leading-relaxed">
          <h2 className="text-2xl font-medium mt-8">Why the early saver wins</h2>
          <p>
            When you save money, that money starts earning interest. In year 2, you earn interest on your money AND on last year&apos;s interest. In year 3, on all three. Interest earning interest earning interest.
          </p>
          <p>
            After 20 years, the interest starts earning way more than the money you&apos;re putting in. After 40 years, the interest is doing 90% of the work. If you skip those first 20 years, you can never catch up — even by tripling what you save later.
          </p>
          <p>
            This is called <strong>compound interest</strong>. Albert Einstein reportedly called it &ldquo;the eighth wonder of the world.&rdquo;
          </p>
          <h2 className="text-2xl font-medium mt-8">The takeaway</h2>
          <p>
            If you&apos;re under 20: even $10 a week matters more than $100 a week will matter when you&apos;re 35.
          </p>
          <p>
            If you&apos;re under 30: you still have massive compound-interest advantage over people 5 years older. Start now.
          </p>
          <p>
            If you&apos;re over 30: the best day to start was 15 years ago. The second-best day is today.
          </p>
        </div>

        <BookStrip category="youth" title="If you liked this lesson" limit={3} />

        {/* Next lesson */}
        <div className="mt-12 flex items-center justify-between border-t border-[#E5E2DC] pt-6">
          <Link to="/learn" className="text-sm text-[#5C6B62] hover:text-[#2B5339]">
            ← All lessons
          </Link>
          <Link
            to="/learn/emergency-fund"
            className="bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#1E3B28] inline-flex items-center gap-2"
          >
            Next: Why an emergency fund is your first goal →
          </Link>
        </div>
      </div>
    </div>
  );
}
